import {
    AlertTriangle,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    LoaderCircle,
    MessageSquareText,
    Search,
    Sparkles,
    ThumbsDown,
    ThumbsUp,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../../References/URLs'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import styles from './KnowledgeBankScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatBoldSegments = (line, keyPrefix = 'b') => {
    const parts = String(line || '').split('**')
    return parts.map((part, index) => (index % 2 === 1 ? <strong key={`${keyPrefix}-${index}`}>{part}</strong> : part))
}

const renderInlineSegments = (line, keyPrefix = 'inline') => {
    const parts = String(line || '').split('`')
    return parts.map((part, index) => {
        if (index % 2 === 1) return <code key={`${keyPrefix}-code-${index}`} className={styles.markdownCodeInline}>{part}</code>
        return formatBoldSegments(part, `${keyPrefix}-bold-${index}`)
    })
}

const renderMarkdownLite = (content) =>
    (() => {
        const lines = String(content || '').split('\n')
        const elements = []
        let bulletBuffer = []

        const flushBullets = () => {
            if (bulletBuffer.length === 0) return
            const listItems = [...bulletBuffer]
            bulletBuffer = []
            elements.push(
                <ul key={`list-${elements.length}`} className={styles.markdownList}>
                    {listItems.map((item, index) => (
                        <li key={`li-${elements.length}-${index}`} className={styles.markdownListItem}>
                            {renderInlineSegments(item, `li-${elements.length}-${index}`)}
                        </li>
                    ))}
                </ul>
            )
        }

        lines.forEach((line, index) => {
            const trimmed = line.trim()
            if (!trimmed) {
                flushBullets()
                return
            }

            if (trimmed.startsWith('- ')) {
                bulletBuffer.push(trimmed.slice(2).trim())
                return
            }

            flushBullets()

            if (trimmed.startsWith('# ')) {
                elements.push(
                    <h1 key={`h1-${index}`} className={styles.markdownH1}>
                        {renderInlineSegments(trimmed.slice(2), `h1-${index}`)}
                    </h1>
                )
                return
            }

            if (trimmed.startsWith('## ')) {
                elements.push(
                    <h2 key={`h2-${index}`} className={styles.markdownH2}>
                        {renderInlineSegments(trimmed.slice(3), `h2-${index}`)}
                    </h2>
                )
                return
            }

            if (trimmed.startsWith('### ')) {
                elements.push(
                    <h3 key={`h3-${index}`} className={styles.markdownH3}>
                        {renderInlineSegments(trimmed.slice(4), `h3-${index}`)}
                    </h3>
                )
                return
            }

            elements.push(
                <p key={`p-${index}`} className={styles.markdownParagraph}>
                    {renderInlineSegments(trimmed, `p-${index}`)}
                </p>
            )
        })

        flushBullets()
        return elements
    })()

const feedbackLabelMap = {
    helpful: 'Yes, this helped',
    notHelpful: 'No, needs better answer',
}

const KBSectionScreen = () => {
    const navigate = useNavigate()
    const { sectionIdentifier } = useParams()
    const locationState = useLocation()?.state || {}
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [sectionData, setSectionData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [expandedArticle, setExpandedArticle] = useState(null)
    const [localFilter, setLocalFilter] = useState('')
    const [fullAnswerMap, setFullAnswerMap] = useState({})
    const [fetchingAnswerMap, setFetchingAnswerMap] = useState({})
    const [feedbackMap, setFeedbackMap] = useState({})

    const viewIdentifierRef = useRef({})
    const viewStartTimeRef = useRef({})
    const scrollDepthRef = useRef({})

    const sectionTitle = sectionData?.section?.sectionTitle || locationState?.sectionName || 'Knowledge Section'

    const fetchSectionArticles = useCallback(async () => {
        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'payKbGetSectionArticles', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    sectionIdentifier,
                }),
            })

            const respo = await response.json()
            if (respo?.status === 'success') {
                setSectionData(respo?.response || null)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Failed to load section.')
            navigate(-1)
        } catch {
            toast.error('Network error while loading section.')
            navigate(-1)
        } finally {
            setIsLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, navigate, sectionIdentifier])

    useEffect(() => {
        if (!sectionIdentifier) {
            toast.error('Section not found.')
            navigate(-1)
            return
        }
        fetchSectionArticles()
    }, [fetchSectionArticles, navigate, sectionIdentifier])

    const sortedArticles = useMemo(() => {
        const base = Array.isArray(sectionData?.articles) ? [...sectionData.articles] : []
        base.sort((first, second) => {
            if (Boolean(second?.isFeatured) !== Boolean(first?.isFeatured)) {
                return Number(Boolean(second?.isFeatured)) - Number(Boolean(first?.isFeatured))
            }
            const secondHelpful = Number(second?.helpfulScore || 0)
            const firstHelpful = Number(first?.helpfulScore || 0)
            if (secondHelpful !== firstHelpful) return secondHelpful - firstHelpful
            return Number(first?.sortOrder || 0) - Number(second?.sortOrder || 0)
        })
        return base
    }, [sectionData?.articles])

    const filteredArticles = useMemo(() => {
        const q = localFilter.trim().toLowerCase()
        if (q.length < 2) return sortedArticles
        return sortedArticles.filter((article) => {
            const question = String(article?.question || '').toLowerCase()
            const summary = String(article?.answerSummary || '').toLowerCase()
            return question.includes(q) || summary.includes(q)
        })
    }, [localFilter, sortedArticles])

    const articleByIdentifier = useMemo(() => {
        const map = {}
        sortedArticles.forEach((article) => {
            if (article?.articleIdentifier) map[article.articleIdentifier] = article
        })
        return map
    }, [sortedArticles])

    const logArticleViewEnd = useCallback(
        async (article) => {
            if (!article?.articleIdentifier) return
            const articleIdentifier = article.articleIdentifier
            const viewIdentifier = viewIdentifierRef.current[articleIdentifier]
            if (!viewIdentifier) return

            const startedAt = viewStartTimeRef.current[articleIdentifier]
            const timeSpentSeconds = startedAt ? Math.max(1, Math.round((Date.now() - startedAt) / 1000)) : 1
            const scrollDepthPercent = scrollDepthRef.current[articleIdentifier] ?? 100

            try {
                await fetch(BASE_URL_API + 'payKbArticleView', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx?.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                        viewIdentifier,
                        articleIdentifier,
                        sectionIdentifier: article?.sectionIdentifier,
                        timeSpentSeconds,
                        scrollDepthPercent,
                        audienceType: 'OWNER',
                        sessionSource: 'LOKACI_PRO',
                        submittedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                    }),
                })
            } catch {
                // intentionally silent telemetry failure
            } finally {
                delete viewIdentifierRef.current[articleIdentifier]
                delete viewStartTimeRef.current[articleIdentifier]
                delete scrollDepthRef.current[articleIdentifier]
            }
        },
        [activeStore?.storeIdentifier, authCtx]
    )

    const fetchFullAnswer = useCallback(
        async (article) => {
            if (!article?.articleIdentifier) return
            if (fullAnswerMap[article.articleIdentifier] || fetchingAnswerMap[article.articleIdentifier]) return

            try {
                setFetchingAnswerMap((prev) => ({ ...prev, [article.articleIdentifier]: true }))
                const response = await fetch(BASE_URL_API + 'payKbGetArticle', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx?.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                        articleIdentifier: article.articleIdentifier,
                        submittedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                        audienceType: 'OWNER',
                        sessionSource: 'LOKACI_PRO',
                    }),
                })

                const respo = await response.json()
                if (respo?.status === 'success') {
                    viewIdentifierRef.current[article.articleIdentifier] = respo?.response?.viewIdentifier || null
                    viewStartTimeRef.current[article.articleIdentifier] = Date.now()
                    scrollDepthRef.current[article.articleIdentifier] = 0
                    setFullAnswerMap((prev) => ({
                        ...prev,
                        [article.articleIdentifier]: String(respo?.response?.answer || ''),
                    }))
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                toast.error(respo?.message || 'Failed to fetch detailed answer.')
            } catch {
                toast.error('Unable to load detailed answer.')
            } finally {
                setFetchingAnswerMap((prev) => ({ ...prev, [article.articleIdentifier]: false }))
            }
        },
        [activeStore?.storeIdentifier, authCtx, fetchingAnswerMap, fullAnswerMap]
    )

    const handleArticleToggle = async (article) => {
        const targetIdentifier = article?.articleIdentifier
        if (!targetIdentifier) return

        if (expandedArticle && expandedArticle !== targetIdentifier) {
            const previousArticle = articleByIdentifier[expandedArticle]
            if (previousArticle) await logArticleViewEnd(previousArticle)
        }

        const nextExpanded = expandedArticle === targetIdentifier ? null : targetIdentifier
        setExpandedArticle(nextExpanded)

        if (nextExpanded === null) {
            await logArticleViewEnd(article)
            return
        }

        if (fullAnswerMap[targetIdentifier]) {
            viewStartTimeRef.current[targetIdentifier] = Date.now()
            if (typeof scrollDepthRef.current[targetIdentifier] !== 'number') {
                scrollDepthRef.current[targetIdentifier] = 0
            }
        }

        if (nextExpanded === targetIdentifier && !fullAnswerMap[targetIdentifier]) {
            await fetchFullAnswer(article)
        }
    }

    useEffect(() => {
        return () => {
            if (!expandedArticle) return
            const article = articleByIdentifier[expandedArticle]
            if (article) {
                logArticleViewEnd(article)
            }
        }
    }, [articleByIdentifier, expandedArticle, logArticleViewEnd])

    const sendFeedback = async (article, feedbackType) => {
        if (!article?.articleIdentifier || !article?.sectionIdentifier) return
        setFeedbackMap((prev) => ({ ...prev, [article.articleIdentifier]: feedbackType }))

        try {
            await fetch(BASE_URL_API + 'payKbLogArticleFeedback', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    articleIdentifier: article.articleIdentifier,
                    sectionIdentifier: article.sectionIdentifier,
                    feedbackType,
                    audienceType: 'OWNER',
                    sessionSource: 'LOKACI_PRO',
                    submittedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                }),
            })
        } catch {
            // intentionally silent telemetry failure
        }

        toast.success(feedbackLabelMap[feedbackType] || 'Thanks for your feedback.')
    }

    return (
        <div className={styles.page}>
            <header className={styles.topBar}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={16} />
                </button>
                <div className={styles.titleWrap}>
                    <span className={styles.topPill}>
                        <Sparkles size={13} /> Help Center
                    </span>
                    <h1>{sectionTitle}</h1>
                </div>
            </header>

            {isLoading ? (
                <section className={styles.resultsWrap}>
                    <div className={styles.searchingRow}>
                        <LoaderCircle size={16} className={styles.spinIcon} />
                        <span className={styles.searchingText}>Loading section articles...</span>
                    </div>
                </section>
            ) : sectionData?.section ? (
                <>
                    <section className={styles.kbSectionMetaWrap}>
                        <p className={styles.kbSectionDescription}>
                            {sectionData?.section?.sectionDescription || 'Helpful guidance for this topic.'}
                        </p>
                        <div className={styles.kbSectionSummaryRow}>
                            <span>
                                {Number(sectionData?.totalArticles || filteredArticles.length || 0)} articles · Most helpful first
                            </span>
                            <span>
                                {Number(sectionData?.section?.searchCount || 0)} searches
                            </span>
                        </div>
                    </section>

                    <section className={styles.searchShell}>
                        <div className={styles.searchBar}>
                            <Search size={17} className={styles.searchIcon} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                value={localFilter}
                                onChange={(event) => setLocalFilter(event.target.value)}
                                placeholder="Search within this section"
                            />
                        </div>
                    </section>

                    <section className={styles.resultsWrap}>
                        <div className={styles.sectionHead}>
                            <MessageSquareText size={15} />
                            <strong>Articles</strong>
                        </div>

                        {filteredArticles.length === 0 ? (
                            <div className={styles.noResultsBox}>
                                <AlertTriangle size={16} />
                                <div>
                                    <strong>No matching articles</strong>
                                    <p>Try a broader keyword for this section.</p>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.resultsList}>
                                {filteredArticles.map((article, index) => {
                                    const isExpanded = expandedArticle === article?.articleIdentifier
                                    const fullAnswer = fullAnswerMap[article?.articleIdentifier]
                                    const isFetching = Boolean(fetchingAnswerMap[article?.articleIdentifier])
                                    const feedbackSelection = feedbackMap[article?.articleIdentifier]
                                    const helpfulScore = Number(article?.helpfulScore || 0)
                                    const viewCount = Number(article?.viewCount || 0)

                                    return (
                                        <article
                                            key={article?.articleIdentifier || index}
                                            className={styles.articleCard}
                                            style={{ animationDelay: `${index * 60}ms` }}
                                        >
                                            {article?.isFeatured ? <span className={styles.kbFeaturedBadge}>★ Featured</span> : null}

                                            <button
                                                type="button"
                                                className={styles.cardHeaderBtn}
                                                onClick={() => handleArticleToggle(article)}
                                            >
                                                <div className={styles.questionWrap}>
                                                    <h3>{article?.question || 'Untitled question'}</h3>
                                                </div>
                                                <span className={styles.expandIcon}>
                                                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                </span>
                                            </button>

                                            <p className={styles.answerSummary}>{article?.answerSummary || 'Summary unavailable.'}</p>

                                            {viewCount > 5 ? (
                                                <div className={styles.kbMetaRow}>
                                                    <span>👍 {helpfulScore.toFixed(0)}%</span>
                                                    <span>·</span>
                                                    <span>{viewCount} views</span>
                                                </div>
                                            ) : null}

                                            {isExpanded ? (
                                                <div
                                                    className={styles.fullAnswerContainer}
                                                    onScroll={(event) => {
                                                        const element = event.currentTarget
                                                        const maxScroll = Math.max(element.scrollHeight - element.clientHeight, 1)
                                                        scrollDepthRef.current[article.articleIdentifier] = Math.round((element.scrollTop / maxScroll) * 100)
                                                    }}
                                                >
                                                    {isFetching ? (
                                                        <div className={styles.searchingRow}>
                                                            <LoaderCircle size={14} className={styles.spinIcon} />
                                                            <span className={styles.searchingText}>Loading answer...</span>
                                                        </div>
                                                    ) : fullAnswer ? (
                                                        <>
                                                            {renderMarkdownLite(fullAnswer)}
                                                            <div className={styles.kbFeedbackRow}>
                                                                <span>Was this helpful?</span>
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.kbFeedbackBtn} ${feedbackSelection === 'helpful' ? styles.kbFeedbackBtnActive : ''}`}
                                                                    onClick={() => sendFeedback(article, 'helpful')}
                                                                >
                                                                    <ThumbsUp size={12} /> Yes
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.kbFeedbackBtn} ${feedbackSelection === 'notHelpful' ? styles.kbFeedbackBtnActive : ''}`}
                                                                    onClick={() => sendFeedback(article, 'notHelpful')}
                                                                >
                                                                    <ThumbsDown size={12} /> No
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <p className={styles.markdownParagraph}>Detailed answer is unavailable for this article.</p>
                                                    )}
                                                </div>
                                            ) : null}
                                        </article>
                                    )
                                })}
                            </div>
                        )}
                    </section>
                </>
            ) : (
                <section className={styles.resultsWrap}>
                    <div className={styles.noResultsBox}>
                        <AlertTriangle size={16} />
                        <div>
                            <strong>Section unavailable</strong>
                            <p>This section is no longer active or could not be loaded.</p>
                        </div>
                    </div>
                </section>
            )}
        </div>
    )
}

export default KBSectionScreen